import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Button, Card, CardContent, Dialog, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import fileDownload from 'js-file-download';
import { LoadingButton } from '@mui/lab';
import { Plus as PlusIcon } from '../../assets/icons/plus';
import CustomTable from '../../components/common/CustomTable';
import { styles } from './styles';
import { columns } from './columns';
import PermissionChecker from '../../components/common/PermissionChecker';
import AuditLogTable from '../../components/common/AuditLog/AuditLogTable';
import {
    clearFilters,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    setFilters,
    setInitialFilters,
} from '../../store/slices/tableController';
import MasterAttributesFilter from './MasterAttributesFilter';
import AddEditMasterAttributes from './AddEditMasterAttributes';
import InfiniteScroll from '../../components/common/InfiniteScroll';
import { Export as ExportIcon } from '../../assets/icons/export';
import { delay } from '../../utils';
import {
    useSearchMasterCustomAttributesMutation,
    useGetMasterCustomAttributesMutation,
    useGetAuditLogsMutation,
} from '../../api/masterAttributes';
import { useExportAuditLogsMutation } from '../../api/download';

const AUDIT_LOG_API_OFFSET = 10;

const MasterAttributes = () => {
    const dispatch = useDispatch();
    const [openDialog, setOpenDialog] = useState(false);
    const [data, setData] = useState([]);
    const [auditLogs, setAuditLogs] = useState({});
    const auditLogHistory = useRef({});
    const controller = useSelector((state) => state.tableController);
    const { enqueueSnackbar } = useSnackbar();
    const [refreshKey, setRefreshKey] = useState(0);

    const [searchMasterCustomAttributes, { isLoading: isSearchLoading }] = useSearchMasterCustomAttributesMutation();
    const [getMasterCustomAttributes, { isLoading: isGetLoading }] = useGetMasterCustomAttributesMutation();
    const [getAuditLogs] = useGetAuditLogsMutation();
    const [exportAuditLogs, { isLoading: isExportLoading }] = useExportAuditLogsMutation();

    const loading = isSearchLoading || isGetLoading;

    const handleRefreshLogs = () => {
        setAuditLogs({});
        auditLogHistory.current = {};
        setRefreshKey((prev) => prev + 1);
    };

    useEffect(() => {
        dispatch(
            setInitialFilters([
                {
                    property: {
                        name: 'attributeName',
                        type: 'string',
                    },
                    operator: {
                        value: '',
                        typesAccepted: [],
                    },
                    value: '',
                },
            ]),
        );
    }, []);

    const fetchData = useCallback(
        async (searched, filters, sort, sortBy, page, pageSize) => {
            try {
                if (searched) {
                    const query = new URLSearchParams({});
                    if (filters.length) {
                        query.append('searchField', filters[0].property);
                        query.append('searchValue', filters[0].value);
                    }
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);
                    const response = await searchMasterCustomAttributes(query).unwrap();
                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);
                    setData(response);
                } else {
                    const query = new URLSearchParams({});
                    if (sortBy) {
                        query.append('sort', sortBy);
                        query.append('order', sort);
                    }
                    query.append('offset', page * pageSize);
                    query.append('max', pageSize);
                    const response = await getMasterCustomAttributes(query).unwrap();
                    if (!response) return;
                    if (response?.status === 'ERROR') throw new Error(response?.message);
                    setData(response);
                }
            } catch (err) {
                if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
                else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
            }
        },
        [searchMasterCustomAttributes, getMasterCustomAttributes, enqueueSnackbar],
    );

    useEffect(() => {
        if (fetchData)
            fetchData(
                controller.searched,
                controller.filters,
                controller.sort,
                controller.sortBy,
                controller.page,
                controller.pageSize,
            );
    }, [
        controller.searched,
        controller.filters,
        controller.sort,
        controller.sortBy,
        controller.page,
        controller.pageSize,
        fetchData,
    ]);

    const handleFiltersApply = (newFilters) => {
        const parsedFilters = newFilters.map((filter) => ({
            property: filter.property.name,
            value: filter.value,
            operator: filter.operator.value,
        }));

        dispatch(setFilters(parsedFilters));
    };

    const handleFiltersClear = () => {
        dispatch(clearFilters());
    };

    const handlePageChange = (newPage) => {
        dispatch(onPageChange(newPage - 1));
    };

    const handlePageSizeChange = (newPageSize) => {
        dispatch(onPageSizeChange(newPageSize));
    };

    const handleSortChange = (event, property) => {
        const isAsc = controller.sortBy === property && controller.sort === 'asc';
        dispatch(
            onSortChange({
                sort: isAsc ? 'desc' : 'asc',
                sortBy: property,
            }),
        );
    };

    const handleFetch = async (page, isLoadingEnd) => {
        try {
            const offsetValue = page * AUDIT_LOG_API_OFFSET;
            let newCommentList = [];
            let totalCount = 0;
            if (auditLogHistory.current[offsetValue]) {
                await delay(10);
                newCommentList = auditLogHistory.current[offsetValue];
                totalCount = auditLogs?.totalCount;
            } else {
                const formData = new FormData();
                formData.append('offset', offsetValue);
                formData.append('max', AUDIT_LOG_API_OFFSET);
                formData.append('name', 'MasterCustomAttribute');
                const response = await getAuditLogs(formData).unwrap();
                if (!response) return;
                if (response?.status === 'ERROR') throw new Error(response?.message);
                newCommentList = response?.commentsList;
                auditLogHistory.current[offsetValue] = newCommentList;
                totalCount = response?.totalCount;
            }

            if (isLoadingEnd) {
                setAuditLogs((logs) => {
                    const prevComments = logs.commentsList || [];
                    const comments =
                        prevComments.length > AUDIT_LOG_API_OFFSET
                            ? prevComments?.slice(prevComments.length - AUDIT_LOG_API_OFFSET)
                            : prevComments;
                    return {
                        commentsList: [...comments, ...newCommentList],
                        totalCount: totalCount,
                    };
                });
            } else {
                setAuditLogs((logs) => {
                    const prevComments = logs.commentsList;
                    const comments = prevComments?.slice(0, AUDIT_LOG_API_OFFSET) || [];
                    return {
                        commentsList: [...newCommentList, ...comments],
                        totalCount: totalCount,
                    };
                });
            }
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const downloadAuditLogs = async () => {
        try {
            const response = await exportAuditLogs().unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            fileDownload(new Blob([response]), 'MasterAttributesAuditLogs.xlsx');
        } catch (error) {
            enqueueSnackbar(error?.message || 'Something went wrong!', {
                variant: 'error',
            });
        }
    };

    return (
        <Box>
            <Box sx={styles.contentHeaderWrapper}>
                <Typography variant="h4">Custom Attributes Masterlist</Typography>
                <PermissionChecker
                    requiredPermissions={['ROLE_BASE_PERMISSION_SYSTEM_INDEX_CREATE_MASTER_CUSTOM_ATTRIBUTE']}
                    noAuthRedirect={false}
                >
                    <Grid item xs="auto">
                        <Button
                            color="primary"
                            onClick={() => setOpenDialog(true)}
                            size="large"
                            startIcon={<PlusIcon fontSize="small" />}
                            variant="contained"
                        >
                            Add Custom Attribute
                        </Button>
                    </Grid>
                    <Dialog
                        open={openDialog}
                        onClose={() => {
                            // setOpenDialog(false);
                        }}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle sx={{ textAlign: 'center' }}>Add Custom Attribute</DialogTitle>
                        <DialogContent>
                            <AddEditMasterAttributes
                                handleClose={() => setOpenDialog(false)}
                                handleAction={() => {
                                    handleRefreshLogs();
                                    setOpenDialog(false);
                                    fetchData(
                                        controller.searched,
                                        controller.filters,
                                        controller.sort,
                                        controller.sortBy,
                                        controller.page,
                                        controller.pageSize,
                                    );
                                }}
                            />
                        </DialogContent>
                    </Dialog>
                </PermissionChecker>
            </Box>
            <Box sx={styles.contentBodyWrapper}>
                <Card>
                    <CardContent>
                        <MasterAttributesFilter
                            disabled={false}
                            filters={controller.filters}
                            onFiltersApply={handleFiltersApply}
                            onFiltersClear={handleFiltersClear}
                            filterProperties={columns}
                            initialFilters={controller.initialFilters}
                        />
                        <CustomTable
                            isLoading={loading}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            onSortChange={handleSortChange}
                            page={controller.page + 1}
                            columns={columns}
                            rows={data?.masterCustomAttributeList || []}
                            rowsCount={data?.masterCustomAttributeCount || 0}
                            sort={controller.sort}
                            sortBy={controller.sortBy}
                            showRowSelector={false}
                            showPagignation={data?.masterCustomAttributeCount > 10}
                            keyColumn="id"
                            action={() => {
                                handleRefreshLogs();
                                fetchData(
                                    controller.searched,
                                    controller.filters,
                                    controller.sort,
                                    controller.sortBy,
                                    controller.page,
                                    controller.pageSize,
                                );
                            }}
                        />
                        <Box>
                            <Grid container spacing={1} justifyContent="space-between" alignItems="center" m={2}>
                                <Typography color="neutral.600" variant="subtitle2">
                                    Audit Logs
                                </Typography>
                                <PermissionChecker
                                    requiredPermissions={[
                                        'ROLE_BASE_PERMISSION_SYSTEM_INDEX_VIEW_MASTER_CUSTOM_ATTRIBUTE',
                                    ]}
                                    noAuthRedirect={false}
                                >
                                    <Grid alignContent="flex-end" mr={4}>
                                        <LoadingButton
                                            loading={isExportLoading}
                                            color="secondary"
                                            disabled={false}
                                            onClick={downloadAuditLogs}
                                            size="medium"
                                            variant="text"
                                            startIcon={<ExportIcon fontSize="small" />}
                                        >
                                            <Typography color="neutral.600" variant="subtitle2">
                                                Export
                                            </Typography>
                                        </LoadingButton>
                                    </Grid>
                                </PermissionChecker>
                            </Grid>
                            <InfiniteScroll
                                key={refreshKey}
                                data={auditLogs?.commentsList || []}
                                offset={AUDIT_LOG_API_OFFSET}
                                totalCount={auditLogs?.totalCount || 0}
                                handleFetch={handleFetch}
                                Component={AuditLogTable}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default MasterAttributes;
